import React from 'react';
import { theme } from '../../theme';

// <purpose>Range readout subtitle rendered below the month-pill row in StatistikSeite.</purpose>
// <dependencies>theme</dependencies>
// <constraints>Contiguous selection → date range + day count. Non-contiguous → month list. Empty → renders nothing.</constraints>

type MonthKey = string; // 'YYYY-MM'

type Props = {
    selectedMonths: MonthKey[];
};

export function isAdjacentMonth(prev: MonthKey, next: MonthKey): boolean {
    const [py, pm] = prev.split('-').map(Number);
    const [ny, nm] = next.split('-').map(Number);
    const prevDate = new Date(py, pm - 1, 1);
    const expected = new Date(prevDate);
    expected.setMonth(expected.getMonth() + 1);
    return expected.getFullYear() === ny && expected.getMonth() === nm - 1;
}

export function monthStart(key: MonthKey): Date {
    const [y, m] = key.split('-').map(Number);
    return new Date(y, m - 1, 1);
}

export function monthEnd(key: MonthKey): Date {
    const start = monthStart(key);
    // Day 0 of next month = last day of this month
    return new Date(start.getFullYear(), start.getMonth() + 1, 0);
}

export function formatMonthShort(key: MonthKey): string {
    const labels = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    const [y, m] = key.split('-').map(Number);
    return `${labels[m - 1]}'${(y % 100).toString().padStart(2, '00')}`;
}

function fmtDate(d: Date): string {
    return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getFullYear()}`;
}

// Why: sum days-in-month per selected month to avoid timezone-offset arithmetic on timestamps
function daysInMonth(key: MonthKey): number {
    const end = monthEnd(key);
    return end.getDate();
}

export function buildReadout(selectedMonths: MonthKey[]): string {
    if (selectedMonths.length === 0) return '';
    const sorted = [...selectedMonths].sort();
    const contiguous = sorted.every((m, i) => i === 0 || isAdjacentMonth(sorted[i - 1], m));
    if (contiguous) {
        const start = monthStart(sorted[0]);
        const end = monthEnd(sorted[sorted.length - 1]);
        const days = sorted.reduce((sum, k) => sum + daysInMonth(k), 0);
        const months = sorted.length;
        return `Zeitraum: ${fmtDate(start)} – ${fmtDate(end)} · ${days} Tage · ${months} Monat${months !== 1 ? 'e' : ''}`;
    }
    return `Zeitraum: ${sorted.map(formatMonthShort).join(' + ')}`;
}

export const DateFilterRangeReadout: React.FC<Props> = ({ selectedMonths }) => {
    const text = buildReadout(selectedMonths);
    if (!text) return null;
    return (
        <p style={{
            fontSize: 10,
            color: theme.colors.textSecondary,
            margin: '6px 0 0 0',
            fontFamily: 'monospace',
        }}>{text}</p>
    );
};
